import type { TFunction } from 'i18next'

export const PREVIEW_THEME_PREFIX = 'preview-'

export const COLOR_THEMES = ['dark'] as const
export const PUBLIC_SEASONAL_THEMES = ['halloween', 'christmas'] as const
export const PREVIEW_THEMES = ['preview-light'] as const
export const PUBLIC_THEMES = [...COLOR_THEMES, ...PUBLIC_SEASONAL_THEMES] as const

export type ColorThemeName = (typeof COLOR_THEMES)[number]
export type PublicSeasonalThemeName = (typeof PUBLIC_SEASONAL_THEMES)[number]
export type PreviewThemeName = (typeof PREVIEW_THEMES)[number]
export type PublicThemeName = (typeof PUBLIC_THEMES)[number]
export type SavedThemeName = PublicThemeName | PreviewThemeName
/** Theme currently applied to the document. */
export type ActiveThemeName = SavedThemeName
/** Holiday attribute for non-color themes. `default` is the plain palette. */
export type HolidayThemeName = 'default' | PublicSeasonalThemeName | PreviewThemeName
/** Calendar themes that apply to every visitor. */
export type CalendarThemeName = 'default' | PublicSeasonalThemeName
/** Public holiday in effect on the calendar, if any. */
export type ActiveHoliday = PublicSeasonalThemeName | null

export type ResolvedTheme = {
  themeName: ActiveThemeName
  activeHoliday: ActiveHoliday
}

export type ResolveActiveThemeInput = {
  now?: Date
  /** Matched route opts out of seasonal decorations. */
  disableSeasonalTheme?: boolean
  seasonalThemesEnabled?: boolean
  selectedTheme?: string | null
  /** Admin accounts may activate registered `preview-` themes. */
  isAdmin?: boolean
}

const PUBLIC_THEME_SET: ReadonlySet<string> = new Set(PUBLIC_THEMES)
const PUBLIC_SEASONAL_THEME_SET: ReadonlySet<string> = new Set(PUBLIC_SEASONAL_THEMES)
const PREVIEW_THEME_SET: ReadonlySet<string> = new Set(PREVIEW_THEMES)

export const hasPreviewThemePrefix = (theme: string): boolean => theme.startsWith(PREVIEW_THEME_PREFIX)

/** True when `themeName` is `name`, or `preview-` plus that name. */
export const isThemeName = (themeName: string | null | undefined, name: string): boolean => {
  if (!(themeName && name)) {
    return false
  }

  const baseName = name.startsWith(PREVIEW_THEME_PREFIX) ? name.slice(PREVIEW_THEME_PREFIX.length) : name
  return themeName === baseName || themeName === `${PREVIEW_THEME_PREFIX}${baseName}`
}

export const isPublicThemeName = (theme: string): theme is PublicThemeName => PUBLIC_THEME_SET.has(theme)

const isPublicSeasonalThemeName = (theme: string): theme is PublicSeasonalThemeName =>
  PUBLIC_SEASONAL_THEME_SET.has(theme)

export const isPreviewThemeName = (theme: string): theme is PreviewThemeName => PREVIEW_THEME_SET.has(theme)

/** Themes a role may choose. Admins also see registered preview themes. */
export const themesSelectableByRole = (isAdmin: boolean): readonly SavedThemeName[] =>
  isAdmin ? [...PUBLIC_THEMES, ...PREVIEW_THEMES] : PUBLIC_THEMES

export type AvailableTheme = {
  name: SavedThemeName
  label: string
}

/** Themes a role may choose, with display names. Admins also see preview themes. */
export const availableThemes = (isAdmin: boolean, t: TFunction): AvailableTheme[] => {
  const labels: Record<SavedThemeName, string> = {
    dark: t('Dark'),
    halloween: t('Halloween'),
    christmas: t('Christmas'),
    'preview-light': t('Light (Preview)'),
  }

  return themesSelectableByRole(isAdmin).map((name) => ({
    name,
    label: labels[name],
  }))
}

/**
 * Public seasonal themes follow the UTC calendar so SSR and the browser activate together.
 * Local `getMonth()` made the server (UTC) switch hours before clients west of UTC.
 * Pass `disableSeasonalTheme` when the matched route opts out; that always returns `default`.
 */
export const getCurrentThemeName = (now: Date = new Date(), disableSeasonalTheme = false): CalendarThemeName => {
  if (disableSeasonalTheme) {
    return 'default'
  }

  const month = now.getUTCMonth() + 1

  if (month === 10) {
    return 'halloween'
  }

  if (month === 12) {
    return 'christmas'
  }

  return 'default'
}

const normalizeSavedTheme = (selectedTheme: string | null | undefined, isAdmin: boolean): SavedThemeName => {
  if (!selectedTheme) {
    return 'dark'
  }

  const canonicalTheme = isThemeName(selectedTheme, 'light') ? 'preview-light' : selectedTheme

  if (hasPreviewThemePrefix(canonicalTheme)) {
    if (isAdmin && isPreviewThemeName(canonicalTheme)) {
      return canonicalTheme
    }
    return 'dark'
  }

  if (isPublicThemeName(canonicalTheme)) {
    return canonicalTheme
  }

  return 'dark'
}

/**
 * Saved theme applies when seasonal themes are off, or when no public season is active.
 * An active public season replaces the saved theme, including for admins.
 */
export const resolveActiveTheme = ({
  now = new Date(),
  disableSeasonalTheme = false,
  seasonalThemesEnabled = true,
  selectedTheme,
  isAdmin = false,
}: ResolveActiveThemeInput = {}): ResolvedTheme => {
  const saved = normalizeSavedTheme(selectedTheme, isAdmin)
  const calendar = getCurrentThemeName(now, false)
  const activeHoliday: ActiveHoliday = calendar === 'default' ? null : calendar

  if (disableSeasonalTheme) {
    return { themeName: isThemeName(saved, 'light') ? 'preview-light' : 'dark', activeHoliday }
  }

  if (seasonalThemesEnabled && activeHoliday) {
    return { themeName: activeHoliday, activeHoliday }
  }

  return { themeName: saved, activeHoliday }
}

/**
 * Logo artwork follows the applied theme.
 * Guests still use the public holiday's logos while that holiday is on the calendar,
 * including when the route or account has seasonal decorations turned off.
 */
export const resolveLogoTheme = ({
  themeName,
  activeHoliday,
  isAuthenticated,
}: {
  themeName: ActiveThemeName
  activeHoliday: ActiveHoliday
  isAuthenticated: boolean
}): ActiveThemeName => {
  if (!isAuthenticated && activeHoliday) {
    return activeHoliday
  }

  return themeName
}

/** `data-holiday` value for the applied theme. Light and dark use the plain palette. */
export const holidayAttributeForTheme = (themeName: ActiveThemeName): HolidayThemeName => {
  if (isThemeName(themeName, 'light') || isThemeName(themeName, 'dark')) {
    return 'default'
  }

  if (isPublicSeasonalThemeName(themeName) || isPreviewThemeName(themeName)) {
    return themeName
  }

  return 'default'
}
